import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Wifi, WifiOff, Power, RefreshCw, Loader2, CheckCircle, Globe, Shield, Building2, Lock, KeyRound, Trash2, Search, X } from 'lucide-react';
import { vpn as vpnApi, workspaces as workspacesApi } from '../services/api/client';
import { toast } from '../utils/toastUtils';

/**
 * VPN Connection Panel Component
 * Provides VPN selection and connection controls for scan wizards
 *
 * Props:
 * - formData: object with use_vpn, vpn_config_id, vpn_username, vpn_password, workspace_id
 * - setFormData: function to update form data
 * - showWorkspaceSelector: boolean - show workspace selection (default: true)
 */
const VPNConnectionPanel = ({ formData, setFormData, showWorkspaceSelector = true }) => {
  const [vpnConfigs, setVpnConfigs] = useState({ interne: [], externe: [] });
  const [loadingVpnConfigs, setLoadingVpnConfigs] = useState(false);
  const [vpnStatus, setVpnStatus] = useState(null);
  const [vpnConnecting, setVpnConnecting] = useState(false);
  const [vpnDisconnecting, setVpnDisconnecting] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  // Search state for VPN filtering
  const [vpnSearchQuery, setVpnSearchQuery] = useState('');
  const [vpnInterneSearchQuery, setVpnInterneSearchQuery] = useState('');

  // State for credential storage
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [storedCredentialsUsername, setStoredCredentialsUsername] = useState('');
  const [loadingStoredCredentials, setLoadingStoredCredentials] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Fetch VPN configs - re-fetch when workspace changes
  useEffect(() => {
    const fetchVpnConfigs = async () => {
      setLoadingVpnConfigs(true);
      try {
        // Pass workspace_id to get workspace-specific VPNs
        const response = await vpnApi.getConfigs(null, formData.workspace_id || null);
        setVpnConfigs({
          interne: response.interne || [],
          externe: response.externe || []
        });
      } catch (error) {
        console.error('Failed to fetch VPN configs:', error);
        setVpnConfigs({ interne: [], externe: [] });
      } finally {
        setLoadingVpnConfigs(false);
      }
    };
    fetchVpnConfigs();
  }, [formData.workspace_id]);

  // Fetch workspaces
  useEffect(() => {
    if (!showWorkspaceSelector) return;
    const fetchWorkspaces = async () => {
      setLoadingWorkspaces(true);
      try {
        const response = await workspacesApi.list(0, 100);
        setWorkspaces(response.workspaces || []);
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
        setWorkspaces([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    };
    fetchWorkspaces();
  }, [showWorkspaceSelector]);

  // Fetch stored VPN credentials when workspace changes
  useEffect(() => {
    const fetchStoredCredentials = async () => {
      if (!formData.workspace_id) {
        setHasStoredCredentials(false);
        setStoredCredentialsUsername('');
        return;
      }

      setLoadingStoredCredentials(true);
      try {
        const response = await vpnApi.getCredentials(formData.workspace_id);
        setHasStoredCredentials(response.has_credentials);
        setStoredCredentialsUsername(response.username || '');

        // If we have stored credentials, auto-fill username and set flag
        if (response.has_credentials) {
          setFormData(prev => ({
            ...prev,
            vpn_username: response.username || prev.vpn_username
          }));
        }
      } catch (error) {
        console.error('Failed to fetch stored credentials:', error);
        setHasStoredCredentials(false);
        setStoredCredentialsUsername('');
      } finally {
        setLoadingStoredCredentials(false);
      }
    };
    fetchStoredCredentials();
  }, [formData.workspace_id]);

  // Fetch VPN status with polling
  const fetchVpnStatus = useCallback(async () => {
    try {
      // Pass workspace_id to get the correct VPN session status
      const status = await vpnApi.getStatus(formData.workspace_id || null);
      setVpnStatus(status);
      return status;
    } catch (error) {
      // Silently handle 401/403 errors - auth interceptor handles redirect
      if (error.response?.status === 401 || error.response?.status === 403 || error.isAuthRedirect) {
        return null;
      }
      // Only log unexpected errors in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch VPN status:', error);
      }
      return null;
    }
  }, [formData.workspace_id]);

  useEffect(() => {
    fetchVpnStatus();
    // Poll VPN status every 5 seconds
    const interval = setInterval(fetchVpnStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchVpnStatus]);

  // Sync formData.use_vpn with actual VPN connection status
  useEffect(() => {
    const isConnected = vpnStatus?.status === 'connected';
    if (isConnected && !formData.use_vpn) {
      // VPN connected but formData says not using VPN - sync it
      setFormData(prev => ({
        ...prev,
        use_vpn: true,
        vpn_config_id: vpnStatus.current_config || prev.vpn_config_id
      }));
    }
  }, [vpnStatus?.status, vpnStatus?.current_config, formData.use_vpn]);

  // Save VPN credentials securely
  const handleSaveCredentials = async () => {
    if (!formData.workspace_id) {
      toast.error('Sélectionnez un workspace pour enregistrer les identifiants');
      return;
    }
    if (!formData.vpn_config_id) {
      toast.error('Sélectionnez une configuration VPN');
      return;
    }
    if (!formData.vpn_username || !formData.vpn_password) {
      toast.error('Renseignez le nom d\'utilisateur et le mot de passe');
      return;
    }

    setSavingCredentials(true);
    try {
      await vpnApi.saveCredentials(
        formData.vpn_config_id,
        formData.vpn_username,
        formData.vpn_password,
        formData.workspace_id
      );
      setHasStoredCredentials(true);
      setStoredCredentialsUsername(formData.vpn_username);
      toast.success('Identifiants VPN enregistres de maniere securisee');
    } catch (error) {
      console.error('Failed to save credentials:', error);
      toast.error('Erreur lors de l\'enregistrement: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingCredentials(false);
    }
  };

  // Delete stored VPN credentials
  const handleDeleteCredentials = async () => {
    if (!formData.workspace_id) return;

    try {
      await vpnApi.deleteCredentials(formData.workspace_id);
      setHasStoredCredentials(false);
      setStoredCredentialsUsername('');
      setSaveCredentials(false);
      toast.success('Identifiants VPN supprimes');
    } catch (error) {
      console.error('Failed to delete credentials:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Load stored credentials for VPN connection
  const loadStoredCredentialsForConnect = async () => {
    if (!formData.workspace_id || !hasStoredCredentials) return null;

    try {
      const response = await vpnApi.getDecryptedCredentials(formData.workspace_id);
      if (response.has_credentials) {
        return {
          username: response.username,
          password: response.password
        };
      }
    } catch (error) {
      console.error('Failed to load stored credentials:', error);
    }
    return null;
  };

  // Connect to VPN
  const handleVpnConnect = async () => {
    if (!formData.vpn_config_id) {
      toast.error('Veuillez selectionner une configuration VPN');
      return;
    }

    setVpnConnecting(true);
    try {
      // Try to use stored credentials if available and no manual credentials provided
      let username = formData.vpn_username || null;
      let password = formData.vpn_password || null;

      if (hasStoredCredentials && (!username || !password)) {
        const storedCreds = await loadStoredCredentialsForConnect();
        if (storedCreds) {
          username = username || storedCreds.username;
          password = password || storedCreds.password;
        }
      }

      await vpnApi.connect(
        formData.vpn_config_id,
        username,
        password,
        formData.workspace_id || null
      );
      toast.success('Connexion VPN en cours...');

      // Save credentials if checkbox is checked
      if (saveCredentials && formData.workspace_id && formData.vpn_username && formData.vpn_password) {
        await handleSaveCredentials();
      }

      // Poll for connection status
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        const status = await fetchVpnStatus();
        if (status?.status === 'connected') {
          clearInterval(pollInterval);
          setVpnConnecting(false);
          toast.success(`VPN connecte! IP: ${status.public_ip || 'inconnue'}`);
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setVpnConnecting(false);
          toast.error('Timeout de connexion VPN');
        }
      }, 2000);
    } catch (error) {
      console.error('VPN connection error:', error);
      toast.error('Erreur de connexion VPN: ' + (error.response?.data?.detail || error.message));
      setVpnConnecting(false);
    }
  };

  // Disconnect from VPN
  const handleVpnDisconnect = async () => {
    setVpnDisconnecting(true);
    try {
      await vpnApi.disconnect(formData.workspace_id || null);
      toast.success('Deconnexion VPN en cours...');

      // Poll for disconnection status
      let attempts = 0;
      const maxAttempts = 15;
      const pollInterval = setInterval(async () => {
        attempts++;
        const status = await fetchVpnStatus();
        if (status?.status !== 'connected') {
          clearInterval(pollInterval);
          setVpnDisconnecting(false);
          toast.success('VPN deconnecte');
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setVpnDisconnecting(false);
          toast.warning('VPN peut encore etre en cours de deconnexion');
        }
      }, 1000);
    } catch (error) {
      console.error('VPN disconnection error:', error);
      toast.error('Erreur de deconnexion VPN: ' + (error.response?.data?.detail || error.message));
      setVpnDisconnecting(false);
    }
  };

  const allVpnConfigs = [...vpnConfigs.interne, ...vpnConfigs.externe];
  const selectedVpnConfig = allVpnConfigs.find(v => v.id === formData.vpn_config_id);

  // Filter VPN externe based on search query
  const filteredExterneVpns = useMemo(() => {
    if (!vpnSearchQuery.trim()) {
      return vpnConfigs.externe;
    }
    const query = vpnSearchQuery.toLowerCase().trim();
    return vpnConfigs.externe.filter(vpn =>
      vpn.name?.toLowerCase().includes(query) ||
      vpn.remote_host?.toLowerCase().includes(query) ||
      vpn.description?.toLowerCase().includes(query)
    );
  }, [vpnConfigs.externe, vpnSearchQuery]);

  // Filter VPN interne based on search query
  const filteredInterneVpns = useMemo(() => {
    if (!vpnInterneSearchQuery.trim()) {
      return vpnConfigs.interne;
    }
    const query = vpnInterneSearchQuery.toLowerCase().trim();
    return vpnConfigs.interne.filter(vpn =>
      vpn.name?.toLowerCase().includes(query) ||
      vpn.remote_host?.toLowerCase().includes(query) ||
      vpn.description?.toLowerCase().includes(query)
    );
  }, [vpnConfigs.interne, vpnInterneSearchQuery]);

  // Check if we need search (more than 3 VPNs for interne, more than 5 for externe)
  const showInterneSearch = vpnConfigs.interne.length > 3;
  const showExterneSearch = vpnConfigs.externe.length > 5;

  // Check if VPN is connected - backend returns status as string "connected"
  const isVpnConnected = vpnStatus?.status === 'connected';

  return (
    <div className="space-y-6">
      {/* Workspace Selector */}
      {showWorkspaceSelector && (
        <div className="bg-brand-black rounded-xl p-5 border-2 border-brand-green/30">
          <label className="block text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-brand-green" />
            Client / Workspace
            <span className="text-xs px-2 py-0.5 bg-brand-green/20 text-brand-green rounded-full">Recommande</span>
          </label>

          {loadingWorkspaces ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Chargement des workspaces...
            </div>
          ) : workspaces.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, workspace_id: '' })}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  formData.workspace_id === ''
                    ? 'border-gray-500 bg-gray-800/50'
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="font-medium text-gray-400 mb-1">Aucun workspace</div>
                <div className="text-xs text-gray-600">Scan sans association client</div>
              </button>

              {workspaces.map((ws) => (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, workspace_id: ws.id })}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    formData.workspace_id === ws.id
                      ? 'border-brand-green bg-brand-green/10'
                      : 'border-gray-700 hover:border-brand-green/50'
                  }`}
                >
                  <div className="font-semibold text-white mb-1 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-brand-green" />
                    {ws.company_name || ws.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {ws.description || 'Workspace client'}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Building2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Aucun workspace disponible</p>
            </div>
          )}
        </div>
      )}

      {/* VPN Configuration */}
      <div className="bg-brand-black rounded-xl p-5 border-2 border-blue-500/30">
        <label className="block text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Wifi className="w-5 h-5 text-blue-400" />
          Configuration VPN
          <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">Reseau Interne</span>
        </label>

        {/* VPN Connection Status & Control Panel */}
        <div className={`mb-4 p-4 rounded-lg border-2 ${
          isVpnConnected
            ? 'bg-green-900/20 border-green-500/50'
            : 'bg-gray-900/30 border-gray-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isVpnConnected ? (
                <>
                  <div className="p-2 bg-green-500/20 rounded-full">
                    <Wifi className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-green-400 font-semibold">VPN connecté</span>
                      {vpnStatus.config_name && (
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 rounded">{vpnStatus.config_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      {vpnStatus.public_ip && (
                        <span>IP : <span className="text-green-300 font-mono">{vpnStatus.public_ip}</span></span>
                      )}
                      {vpnStatus.country && (
                        <span>{vpnStatus.country}</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-2 bg-gray-700/50 rounded-full">
                    <WifiOff className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <span className="text-gray-400 font-medium">VPN non connecté</span>
                    <p className="text-xs text-gray-500 mt-0.5">Sélectionnez un VPN et connectez-vous</p>
                  </div>
                </>
              )}
            </div>

            {/* VPN Connect/Disconnect Button */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchVpnStatus}
                className="p-2 text-gray-500 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Rafraichir le statut"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              {isVpnConnected ? (
                <button
                  type="button"
                  onClick={handleVpnDisconnect}
                  disabled={vpnDisconnecting}
                  className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {vpnDisconnecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Déconnexion...
                    </>
                  ) : (
                    <>
                      <Power className="w-4 h-4" />
                      Déconnecter
                    </>
                  )}
                </button>
              ) : formData.use_vpn && formData.vpn_config_id ? (
                <button
                  type="button"
                  onClick={handleVpnConnect}
                  disabled={vpnConnecting}
                  className="px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {vpnConnecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    <>
                      <Power className="w-4 h-4" />
                      Connecter VPN
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>

          {/* Confirmation message when VPN is connected */}
          {isVpnConnected && (
            <div className="mt-3 pt-3 border-t border-green-500/20">
              <div className="flex items-center gap-2 text-xs text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>Le scan utilisera cette connexion VPN pour acceder au reseau client</span>
              </div>
            </div>
          )}
        </div>

        {/* VPN Config Selection */}
        {loadingVpnConfigs ? (
          <div className="flex items-center gap-2 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Chargement des configurations VPN...
          </div>
        ) : vpnConfigs.interne.length > 0 || vpnConfigs.externe.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-3">
              {/* Option: Pas de VPN */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, use_vpn: false, vpn_config_id: '' })}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  !formData.use_vpn
                    ? 'border-gray-500 bg-gray-800/50'
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="font-medium text-gray-400 mb-1 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Scan Externe (Sans VPN)
                </div>
                <div className="text-xs text-gray-600">Scanner la cible depuis Internet</div>
              </button>

              {/* VPN Interne Section */}
              {vpnConfigs.interne.length > 0 && (
                <div className="col-span-full">
                  {/* Section Header with Search */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Wifi className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-blue-400">
                        VPN Reseau Interne ({vpnConfigs.interne.length})
                      </span>
                    </div>

                    {/* Search Field - only show if more than 3 VPNs */}
                    {showInterneSearch && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text"
                          value={vpnInterneSearchQuery}
                          onChange={(e) => setVpnInterneSearchQuery(e.target.value)}
                          placeholder="Rechercher un VPN interne..."
                          className="pl-9 pr-8 py-1.5 bg-brand-black border border-blue-500/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 w-64"
                        />
                        {vpnInterneSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setVpnInterneSearchQuery('')}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Search results info */}
                  {showInterneSearch && vpnInterneSearchQuery && (
                    <div className="text-xs text-gray-500 mb-2">
                      {filteredInterneVpns.length} resultat(s) pour "{vpnInterneSearchQuery}"
                    </div>
                  )}

                  {/* VPN Interne List - with max height and scroll if many */}
                  <div className={`grid grid-cols-1 gap-2 ${vpnConfigs.interne.length > 3 ? 'max-h-48 overflow-y-auto pr-2' : ''}`}>
                    {filteredInterneVpns.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucun VPN trouve pour "{vpnInterneSearchQuery}"</p>
                      </div>
                    ) : (
                      filteredInterneVpns.map((vpn) => (
                        <button
                          key={vpn.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, use_vpn: true, vpn_config_id: vpn.id })}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            formData.use_vpn && formData.vpn_config_id === vpn.id
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-gray-700 hover:border-blue-500/50'
                          }`}
                        >
                          <div className="font-semibold text-white mb-1 flex items-center gap-2">
                            <Wifi className="w-4 h-4 text-blue-400" />
                            {vpn.name}
                            <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Interne</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {vpn.remote_host && `Serveur: ${vpn.remote_host}`}
                            {vpn.remote_port && `:${vpn.remote_port}`}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* VPN Externe Section */}
              {vpnConfigs.externe.length > 0 && (
                <div className="col-span-full">
                  {/* Section Header with Search */}
                  <div className="flex items-center justify-between mb-3 mt-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-medium text-purple-400">
                        VPN Externes ({vpnConfigs.externe.length})
                      </span>
                    </div>

                    {/* Search Field - only show if more than 5 VPNs */}
                    {showExterneSearch && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="text"
                          value={vpnSearchQuery}
                          onChange={(e) => setVpnSearchQuery(e.target.value)}
                          placeholder="Rechercher un VPN..."
                          className="pl-9 pr-8 py-1.5 bg-brand-black border border-purple-500/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 w-64"
                        />
                        {vpnSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setVpnSearchQuery('')}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Search results info */}
                  {showExterneSearch && vpnSearchQuery && (
                    <div className="text-xs text-gray-500 mb-2">
                      {filteredExterneVpns.length} resultat(s) pour "{vpnSearchQuery}"
                    </div>
                  )}

                  {/* VPN Externe List - with max height and scroll if many */}
                  <div className={`grid grid-cols-1 gap-2 ${vpnConfigs.externe.length > 6 ? 'max-h-64 overflow-y-auto pr-2' : ''}`}>
                    {filteredExterneVpns.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucun VPN trouve pour "{vpnSearchQuery}"</p>
                      </div>
                    ) : (
                      filteredExterneVpns.map((vpn) => (
                        <button
                          key={vpn.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, use_vpn: true, vpn_config_id: vpn.id })}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            formData.use_vpn && formData.vpn_config_id === vpn.id
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-gray-700 hover:border-purple-500/50'
                          }`}
                        >
                          <div className="font-semibold text-white mb-1 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-purple-400" />
                            {vpn.name}
                            <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">Externe</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {vpn.remote_host && `Serveur: ${vpn.remote_host}`}
                            {vpn.remote_port && `:${vpn.remote_port}`}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* VPN Credentials if required */}
            {formData.use_vpn && formData.vpn_config_id && selectedVpnConfig?.requires_auth && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                {/* Stored credentials indicator */}
                {hasStoredCredentials && formData.workspace_id && (
                  <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400 font-medium">
                          Identifiants enregistres
                        </span>
                        {storedCredentialsUsername && (
                          <span className="text-xs text-gray-400">
                            ({storedCredentialsUsername})
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleDeleteCredentials}
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                        title="Supprimer les identifiants enregistres"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Les identifiants seront utilises automatiquement pour la connexion VPN
                    </p>
                  </div>
                )}

                {loadingStoredCredentials ? (
                  <div className="flex items-center gap-2 text-gray-400 mb-3">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verification des identifiants enregistres...
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-2">
                          Nom d'utilisateur VPN
                        </label>
                        <input
                          type="text"
                          value={formData.vpn_username || ''}
                          onChange={(e) => setFormData({ ...formData, vpn_username: e.target.value })}
                          placeholder={hasStoredCredentials ? "(utiliser identifiants enregistrés)" : "utilisateur"}
                          className="w-full px-3 py-2 bg-brand-black border border-blue-500/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-2">
                          Mot de passe VPN
                        </label>
                        <input
                          type="password"
                          value={formData.vpn_password || ''}
                          onChange={(e) => setFormData({ ...formData, vpn_password: e.target.value })}
                          placeholder={hasStoredCredentials ? "(utiliser identifiants enregistrés)" : "********"}
                          className="w-full px-3 py-2 bg-brand-black border border-blue-500/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                    </div>

                    {/* Save credentials checkbox - only show if workspace is selected */}
                    {formData.workspace_id && !hasStoredCredentials && (
                      <div className="mt-3 p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={saveCredentials}
                            onChange={(e) => setSaveCredentials(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded border-amber-500/50 bg-brand-black text-amber-500 focus:ring-amber-500/50 focus:ring-offset-0"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <KeyRound className="w-4 h-4 text-amber-400" />
                              <span className="text-sm font-medium text-amber-400">
                                Enregistrer les identifiants de facon securisee
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Les identifiants seront chiffres (AES-256) et stockes en base de donnees.
                              Ils seront automatiquement utilises lors des prochaines connexions VPN pour ce workspace.
                            </p>
                          </div>
                        </label>

                        {/* Manual save button */}
                        {saveCredentials && formData.vpn_username && formData.vpn_password && (
                          <button
                            type="button"
                            onClick={handleSaveCredentials}
                            disabled={savingCredentials}
                            className="mt-3 px-3 py-1.5 bg-amber-500/20 text-amber-400 border border-amber-500/50 rounded-lg hover:bg-amber-500/30 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                          >
                            {savingCredentials ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Enregistrement...
                              </>
                            ) : (
                              <>
                                <Lock className="w-3 h-3" />
                                Enregistrer maintenant
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {formData.use_vpn && (
              <div className="mt-4 bg-blue-900/20 border border-blue-500/20 rounded p-3 text-xs text-gray-400">
                <p className="text-blue-400 font-semibold mb-1">Scan via VPN:</p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>Le VPN sera établi avant le démarrage du scan</li>
                  <li>Tous les outils scanneront via le tunnel VPN</li>
                  <li>Accès aux IPs internes (192.168.x.x, 10.x.x.x, etc.)</li>
                  <li>Le VPN sera déconnecté à la fin du scan</li>
                </ul>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <Wifi className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Aucune configuration VPN disponible</p>
            <p className="text-xs mt-1">Le scan sera effectue depuis Internet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VPNConnectionPanel;
